import { Box, Tab, Tabs } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import useGet from "../../../services/hooks/useGet";
import {
  ICase,
  ITemplateDataTypes,
} from "../../../services/interfaces/DataInterfaces";
import { apiEndpoints } from "../../../shared/constants/api-constant";
import { Button, Heading } from "../../../shared/ReusableComponents";
import ArrowBack from "../../../shared/ReusableComponents/ArrowBack";
import { CommonFunctions } from "../../../shared/utils/CommonFunctions";
import {
  hideLoader,
  setCaseDetailCurrentNavIndex,
  showLoader,
} from "../../../store/currentPreferencesSlice";
import { Medications as StudentMedication } from "../../student/Medications";
import Investigation from "./Investigation";
import PatientCard from "./PatientCard";
import ProgressNotes from "./ProgressNotes";
import { Vital } from "./Vital";
import IconButton from "../../../shared/ReusableComponents/IconButton";
import { AdminMedications } from "../../admin/Medications/AdminMedications";
import { AdminInvestigation } from "../../admin/Investigation/AdminInvestigation/AdminInvestigation";
import { AlertType } from "../../../shared/constants/enums";
import { RiskAssessment } from "../RiskAssessment/RiskAssessment";
import { AdminFluidOrderNav } from "../../admin/FluidOrder/AdminFluidOrderNav";
import { StudentFluidOrderNav } from "../../student/FluidOrder/StudentFluidOrderNav";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { axiosInstance } from "../../../services/restApiService";
import { toast } from "../../../store/toastSlice";
import { AppConfigurationHelper } from "../../../services/AppConfigurationHelper";

export const CaseDetails = () => {
  const { currentCaseId, currentTemplateId } = useParams<{
    currentCaseId: string;
    currentTemplateId: string;
  }>();
  const [patientData, setPatientData] = useState<ICase | null>(null);
  const [templateData, setTemplateData] = useState<ITemplateDataTypes | null>(
    null
  );
  const { loading, error, data, getService } = useGet();
  const rootStore = useSelector((state: any) => state);
  const currentNavIndex =
    rootStore?.currentPreferences?.caseDetailCurrentNavIndex;
  const isCurrentUserStudent = CommonFunctions.IsCurrentUserStudent();
  const isCurrentSubjectCoordinator =
    CommonFunctions.IsCurrentUserSubCoordinator();

  const isCurrentSuperUser = CommonFunctions.IsCurrentSuperUser();
  const isCurrentTutor = CommonFunctions.IsCurrentUserTutor();
  const isCurrentUserStudentOrTutor = isCurrentUserStudent || isCurrentTutor;

  const dispatch = useDispatch();
  const navigator = useNavigate();

  const getId = (caseOrTemplateData: ICase | ITemplateDataTypes) => {
    return isCurrentUserStudentOrTutor
      ? (caseOrTemplateData as ICase)?.id
      : (caseOrTemplateData as ITemplateDataTypes)?.id;
  };

  const GetTabs = (caseId: number, templateId?: number | any) => {
    return [
      {
        Text: "Progress Notes",
        Component: <ProgressNotes parentId={caseId} />,
      },
      {
        Text: "Medications",
        Component: isCurrentUserStudentOrTutor ? (
          <StudentMedication id={caseId} />
        ) : isCurrentSubjectCoordinator || isCurrentSuperUser ? (
          <AdminMedications templateId={templateId} />
        ) : null,
      },
      {
        Text: "Assessment",
        Component: (
          <RiskAssessment
            isCurrentUserStudentOrTutor={isCurrentUserStudentOrTutor}
            parentId={isCurrentUserStudentOrTutor ? caseId : templateId ?? 0}
          />
        ),
      },
      {
        Text: "Vitals",
        Component: (
          <Vital
            parentId={caseId}
            isCurrentUserStudentOrTutor={isCurrentUserStudentOrTutor}
          />
        ),
      },
      {
        Text: "Investigation",
        Component: isCurrentUserStudentOrTutor ? (
          <Investigation />
        ) : templateId ? (
          <AdminInvestigation templateId={templateId} />
        ) : null,
      },
      {
        Text: "Fluids",
        Component: isCurrentUserStudentOrTutor ? (
          <StudentFluidOrderNav caseId={caseId} />
        ) : templateId ? (
          <AdminFluidOrderNav templateId={templateId} />
        ) : null,
      },
    ]; // don't change the order of the tabs
  };

  useEffect(() => {
    if ((!currentCaseId && !currentTemplateId) || error) return;
    const fetchData = async () => {
      isCurrentUserStudentOrTutor
        ? !(data && patientData) &&
          getService(`${apiEndpoints.CASEDETAILS}/${currentCaseId}`)
        : !(data && templateData) &&
          getService(`${apiEndpoints.TEMPLATE}/${currentTemplateId}`);
    };
    dispatch(setCaseDetailCurrentNavIndex(0));
    fetchData();
  }, [currentCaseId, currentTemplateId]);

  useEffect(() => {
    if (data) {
      setPatientData(data);
      setTemplateData(data);
    }
  }, [data]);

  useEffect(() => {
    loading && dispatch(showLoader());
    !loading && dispatch(hideLoader());
  }, [loading]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    dispatch(setCaseDetailCurrentNavIndex(newValue));
  };
  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!patientData || !templateData) {
    return <div>Patient not found</div>;
  }

  const handleExportButtonClick = async () => {
    if (!currentCaseId) return;
    const timezoneInMinutes = -new Date().getTimezoneOffset();

    try {
      dispatch(showLoader());
      const response = await axiosInstance.get(
        `${apiEndpoints.CASE_REPORT}/${currentCaseId}/report`,
        {
          responseType: "blob",
          params: {
            timezone: timezoneInMinutes,
            maxHoursForReport:
              AppConfigurationHelper.Instance().maxHoursForReport,
            isMissedIncluded:
              AppConfigurationHelper.Instance().isMissedIncluded,
          },
        }
      );
      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);
      const fileName = `${CommonFunctions.ConvertIdToDisplayId(
        Number(currentCaseId),
        "case"
      )}_Report.pdf`;

      CommonFunctions.downloadFileFromLink(url, fileName);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.data?.message ||
        (error?.code?.includes("ERR_NETWORK")
          ? "Connection timed out. Please try again later."
          : "Unable to download the PDF report. Please try again.");
      dispatch(
        toast({
          isOpen: true,
          message: errorMessage,
          type: AlertType.ERROR,
        })
      );
    } finally {
      dispatch(hideLoader());
    }
  };

  return (
    <React.Fragment>
      {isCurrentUserStudentOrTutor ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <ArrowBack
            className="h3"
            text={`${patientData.patientFirstName} ${patientData.patientLastName}`}
          />
          <Button
            text="Export as PDF"
            fullWidth={false}
            variant="contained"
            type="button"
            onClick={handleExportButtonClick}
            startIcon={<PictureAsPdfIcon />}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <ArrowBack
            onClick={() => {
              isCurrentSuperUser
                ? navigator(`/admin/templatelibrary`)
                : navigator(`/coordinator`);
            }}
            className="h3"
            text={
              <React.Fragment>
                <span className="header-title">
                  {CommonFunctions.ConvertIdToDisplayId(
                    getId(templateData),
                    "template"
                  )}
                </span>
                <span
                  title={templateData.templateDescription}
                  className="header-description"
                >
                  {templateData?.templateDescription?.length <= 80
                    ? templateData?.templateDescription
                    : `${templateData?.templateDescription.substring(
                        0,
                        80
                      )}...`}
                </span>
              </React.Fragment>
            }
          />
          {isCurrentSuperUser && (
            <IconButton
              className="template-edit-icon"
              onClick={() =>
                navigator(`/admin/createtemplate/${currentTemplateId}`)
              }
              text="Edit Template"
              icon={<span className="material-symbols-outlined"> edit </span>}
            />
          )}
        </Box>
      )}
      <PatientCard
        data={isCurrentUserStudentOrTutor ? patientData : templateData}
      />
      <Box className="box-grid">
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={currentNavIndex}
            onChange={handleTabChange}
            textColor="primary"
            color="primary"
            aria-label="case-details tabs"
          >
            {GetTabs(getId(patientData), getId(templateData)).map(
              (tab, index) => (
                <Tab
                  key={index}
                  value={index}
                  label={
                    <Heading
                      type={"h5"}
                      text={tab.Text}
                      classes="sentence-case"
                      isBold={currentNavIndex === index}
                    />
                  }
                />
              )
            )}
          </Tabs>
        </Box>
        <Box sx={{ marginTop: "20px" }}>
          {GetTabs(getId(patientData), getId(templateData)).map(
            (tab, index) =>
              currentNavIndex === index && (
                <Box key={index} role="tabpanel">
                  {tab.Component ? tab.Component : tab.Text}
                </Box>
              )
          )}
        </Box>
      </Box>
    </React.Fragment>
  );
};

export default CaseDetails;
